import { useTheme, Fade, type DialogProps, Typography } from '@mui/material';
import { type FadeProps } from '@mui/material/Fade';
import { XIcon } from '@phosphor-icons/react/dist/ssr/X';
import { Button, IconButton, type ButtonProps } from '@tgtg/kit';
import { type ReactNode, forwardRef, type ReactElement } from 'react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl';

import msg from './ActionDialogMessages';
import { StyledDialog, StyledHeaderAndContent, StyledDialogActions } from './StyledComponents';

type DialogPropsNoOpen = Omit<DialogProps, 'open'>;

export interface ActionDialogProps extends DialogPropsNoOpen {
  hideDefaultDialogActions?: boolean;
  open: boolean;
  onDismiss?: () => void;
  size?: 'sm' | 'lg';
  dialogTitle?: ReactNode | string;
  dialogContent?: ReactNode | string;
  action1ButtonLabel?: ReactElement;
  action1ButtonAriaLabel?: MessageDescriptor;
  action1ButtonProps?: Omit<ButtonProps, 'children'>;
  onAction1Click?: () => void;
  action2ButtonLabel?: ReactElement;
  action2ButtonAriaLabel?: MessageDescriptor;
  action2ButtonProps?: Omit<ButtonProps, 'children'>;
  onAction2Click?: () => void;
  dismissButtonLabel?: ReactElement;
  revertButtons?: boolean;
  centerButtons?: boolean;
  children?: ReactNode;
  isSubmitting?: boolean;
  disabled?: boolean;
  fullHeight?: boolean;
  zIndex?: number;
}
const Transition = forwardRef<HTMLDivElement, FadeProps & { children: ReactElement }>(
  (props, ref) => <Fade in ref={ref} {...props} />,
);

const ActionDialog = ({
  hideDefaultDialogActions,
  open,
  dialogTitle = '',
  dialogContent = '',
  children,
  onDismiss,
  action1ButtonLabel,
  action1ButtonAriaLabel,
  action1ButtonProps = {},
  onAction1Click,
  action2ButtonLabel,
  action2ButtonAriaLabel,
  action2ButtonProps = {},
  onAction2Click,
  isSubmitting = false,
  dismissButtonLabel,
  disabled = false,
  fullHeight = false,
  TransitionComponent = Transition,
  zIndex,
  size = 'lg',
  ...others
}: ActionDialogProps) => {
  const { zIndex: zIndexTheme } = useTheme();
  const displayHeaderAndContent = dialogTitle || dialogContent;

  const { formatMessage } = useIntl();

  return (
    <StyledDialog
      open={open}
      size={size}
      fullHeight={fullHeight}
      TransitionComponent={TransitionComponent}
      onClose={onDismiss}
      style={zIndex ? { zIndex: zIndexTheme.modal + zIndex } : undefined}
      {...others}
    >
      <StyledHeaderAndContent>
        <>
          {displayHeaderAndContent && (
            <>
              {dialogTitle ? (
                <>
                  {onDismiss && (
                    <IconButton
                      aria-label={formatMessage(msg.closeDialogAriaLabelA11y)}
                      onClick={onDismiss}
                      style={{ alignSelf: 'flex-end' }}
                    >
                      <XIcon width={24} height={24} />
                    </IconButton>
                  )}
                  {dialogTitle && (
                    <Typography variant='headingSmall' id='dialogTitle'>
                      {dialogTitle}
                    </Typography>
                  )}
                </>
              ) : null}
            </>
          )}
          {children}
        </>
      </StyledHeaderAndContent>
      {!hideDefaultDialogActions && (
        <StyledDialogActions>
          {(onAction1Click || action1ButtonProps.type === 'submit') && action1ButtonLabel && (
            <Button
              onClick={onAction1Click}
              isLoading={isSubmitting}
              isDisabled={disabled}
              aria-label={
                action1ButtonAriaLabel ? formatMessage({ ...action1ButtonAriaLabel }) : undefined
              }
              {...action1ButtonProps}
            >
              {action1ButtonLabel}
            </Button>
          )}
          {onAction2Click && action2ButtonLabel && (
            <Button
              variant='transparent'
              onClick={onAction2Click}
              isLoading={isSubmitting}
              isDisabled={disabled}
              aria-label={
                action2ButtonAriaLabel ? formatMessage({ ...action2ButtonAriaLabel }) : undefined
              }
              {...action2ButtonProps}
            >
              {action2ButtonLabel}
            </Button>
          )}
          {!onAction2Click && onDismiss && (
            <Button
              variant='transparent'
              onClick={onDismiss}
              aria-label={formatMessage({ ...msg.cancelDialogAriaLabelA11y })}
            >
              {dismissButtonLabel ?? <FormattedMessage {...msg.cancelButtonLabel} />}
            </Button>
          )}
        </StyledDialogActions>
      )}
    </StyledDialog>
  );
};

export default ActionDialog;
